import { create } from 'zustand';
import type { SubtitleItem, SubtitleSize } from '@/types/subtitle';
import type { ErrorInfo } from '@/utils/playerErrorCodes';
import {
  processSubtitleUrl,
  isVTTFormat,
  calculateSubtitlePosition,
  createSubtitleStyle,
  loadSubtitlePreferences,
  saveSubtitleLanguagePreference,
  saveSubtitleFontsizePreference,
  getSubtitleIndex,
  filterSubtitlesByChannel,
  createNoSubtitleItem,
  getSubtitleSizeConfig,
} from '@/utils/subtitleUtils';
import { internalToDisplayQuality, getQualityWeight, INTERNAL_TO_DISPLAY_QUALITY_MAP } from '@/utils/constants';
import { toast } from '@/components/common';
import i18n from '@/i18n/config';
import { getNextEpisode } from '@/utils/nextEpisodeUtils';
import { logger } from '@/utils/devUtils';

/**
 * 播放实例对象的状态
 */
export enum PlayerInstanceVideoState {
  Idle = 'idle',
  LoadStart = 'loadStart',
  /** 当浏览器能够开始播放指定的音频/视频时，但尚未开始播放 */
  CanPlay = 'canplay',
  Playing = 'playing',
  Pause = 'pause',
  Seeking = 'seeking',
  Seeked = 'seeked',
  Buffering = 'buffering',
  /** 正常播放结束（包括外部中断播放），如果是播放出错则没有此状态 */
  End = 'end',
}

/**
 * 播放器状态管理 Store
 * 管理播放状态、设置状态，并提供播放器控制接口
 */

// ==================== localStorage 存储键名常量 ====================
export const PLAYER_STORAGE_PREFIX = 'player-';

export const PLAYER_STORAGE_KEYS = {
  /** 默认画质（内部格式如 'fhd', 'shd', 'hd', 'sd'） */
  QUALITY: `${PLAYER_STORAGE_PREFIX}quality`,
  /** 播放器内核 ('hls.js' | 'origin') */
  KERNEL: `${PLAYER_STORAGE_PREFIX}kernel`,
  /** 跳过片头片尾开关 ('1' 开启, '0' 关闭) */
  SKIP_INTRO: `${PLAYER_STORAGE_PREFIX}skipIntro`,
  /** 画面比例 ('0' 原始比例, '1' 全屏幕) */
  ASPECT_RATIO: `${PLAYER_STORAGE_PREFIX}aspectRatio`,
} as const;

// ==================== 常量 ====================
const isBrowser = typeof window !== 'undefined';

/** 有效的内部画质格式 */
const VALID_INTERNAL_QUALITIES = Object.keys(INTERNAL_TO_DISPLAY_QUALITY_MAP);

/** 默认画质（内部格式） */
const DEFAULT_QUALITY = 'shd';

// ==================== 类型定义 ====================

export interface OperationResult {
  success: boolean;
  message?: string;
  error?: Error;
}

// 清晰度选项（内部格式如 'fhd', 'shd', 'hd', 'sd', 'ld' 等）
export type Quality = string;

// 播放倍速
export type PlaybackSpeed = '0.5x' | '0.75x' | '1x' | '1.25x' | '1.5x' | '2x';

// 字幕语言（兼容旧类型，保留简化版）
export type SubtitleLanguage = 'en' | 'zh' | 'ja' | 'ko' | 'off';

// 音频语言
export type AudioLanguage = 'en' | 'zh' | 'ja' | 'ko';

/**
 * 清晰度定义项
 */
export interface DefinitionItem {
  id?: number;
  name: string; // 播放器内部格式（如 'fhd', 'shd', 'hd', 'sd', 'ld'）
  cname?: string; // 中文名称（如 '1080P', '720P', '480P'）
  resolution?: string; // 分辨率（如 '1080P', '720P', '480P'）
  [key: string]: unknown; // 允许其他字段
}

export interface PlayerState {
  // ==================== 播放状态 ====================
  /** 播放状态由播放器内部维护，通过 getPlaybackState() 从 playerInstance.currentVideoInfo.state 读取 */
  playbackStateVersion: number;
  setPlaybackStateVersion: () => void;
  getPlaybackState: () => PlayerInstanceVideoState | string | undefined;

  // 当前播放时间（秒）
  currentTime: number;
  setCurrentTime: (time: number) => void;

  // 总时长（秒）
  totalTime: number;
  setTotalTime: (time: number) => void;

  // 缓冲进度（0-1）
  buffered: number;
  setBuffered: (progress: number) => void;

  // 当前视频信息
  cid: string | null;
  vid: string | null;
  payStatus: number | null;
  videoTitle: string;
  videoInfo: any | null; // 视频详细信息（直接是 parseData，包含 definitionList、subtitleList、duration、title 等）
  videoListInfo: any | null; // 播放列表信息（包含 play_list_info）
  isOnError: boolean;
  setIsOnError: (isOnError: boolean) => void;
  setPreviewTime: (time: number) => void;
  previewTime: number;
  setVideoInfo: (cid: string, vid: string, title?: string) => void;
  setVideoDetailInfo: (info: any) => void; // 设置视频详细信息（直接保存 parseData）
  setVideoListInfo: (info: any | null) => void; // 设置播放列表信息
  setPayStatus: (payStatus: number | null) => void;
  resetError: () => void;
  // ==================== 播放控制 ====================
  // 播放/暂停
  play: () => Promise<OperationResult>;
  pause: () => Promise<OperationResult>;
  togglePlayPause: () => Promise<OperationResult>;
  stop: () => Promise<OperationResult>; // 停止播放

  // 跳转进度
  seek: (time: number) => void;
  seekRelative: (seconds: number) => void; // 相对跳转（快进/快退）

  // 下一集/上一集
  playNext: () => { success: boolean; cid?: string; vid?: string; message?: string; hasNextEpisode?: boolean } | null;
  playPrevious: () => { success: boolean; cid?: string; vid?: string; message?: string } | null;
  hasNextEpisode: boolean;
  setHasNextEpisode: (hasNextEpisode: boolean) => void;

  // ==================== 设置状态 ====================
  // 清晰度
  quality: Quality;
  setQuality: (options: { quality: Quality; showToast?: boolean }) => Promise<OperationResult>;

  // 播放倍速
  speed: PlaybackSpeed;
  setSpeed: (speed: PlaybackSpeed) => Promise<OperationResult>;

  // ==================== 字幕功能（完整实现）====================
  // 字幕列表（包含"无字幕"选项）
  subtitleList: SubtitleItem[];

  // 当前选中的字幕索引
  currentSubtitleIndex: number;

  // 字幕大小
  subtitleSize: SubtitleSize;

  // 初始化字幕列表
  initSubtitleList: (list: SubtitleItem[], channelId?: string) => void;

  // 切换字幕语言
  setSubtitleLanguage: (index: number) => Promise<OperationResult>;

  // 设置字幕大小
  setSubtitleSize: (size: SubtitleSize) => Promise<OperationResult>;

  // 应用字幕设置到播放器
  applySubtitleSettings: () => void;

  // 加载用户偏好
  loadSubtitlePreferences: () => void;

  // 清除字幕状态
  clearSubtitleState: () => void;

  // ==================== 字幕兼容字段（保留旧接口）====================
  // 字幕设置（简化版，保留兼容性）
  subtitleEnabled: boolean;
  subtitleLanguage: SubtitleLanguage;
  setSubtitleEnabled: (enabled: boolean) => Promise<OperationResult>;

  // 音频语言
  audioLanguage: AudioLanguage;
  setAudioLanguage: (lang: AudioLanguage) => Promise<OperationResult>;

  // 跳过片头片尾（秒，后台返回的都是秒）
  skipEnd: number; // 片尾开始时间点（对应 skip_end，秒）
  skipHeadBegin: number; // 片头开始时间（对应 skip_head_begin，秒）
  skipStart: number; // 片头结束时间点（对应 skip_start，秒）
  skipTailEnd: number; // 片尾结束时间（对应 skip_tail_end，秒）
  setSkipIntro: (skipEnd: number, skipHeadBegin: number, skipStart: number, skipTailEnd: number) => void;

  // ==================== 播放器实例 ====================
  // 播放器实例引用（内部使用，不直接暴露）
  playerInstance: any | null;
  setPlayerInstance: (instance: any | null) => void;

  // ==================== 播放器事件管理 ====================
  // 监听播放器事件
  onPlayerEvent: (eventName: string, handler: (...args: any[]) => void) => void;

  // 移除播放器事件监听
  offPlayerEvent: (eventName: string, handler: (...args: any[]) => void) => void;

  // 触发播放器事件
  emitPlayerEvent: (eventName: string, ...args: any[]) => void;

  // ==================== 播放器配置 ====================
  // 播放器内核（hls.js | origin）
  playerKernel: 'hls.js' | 'origin';
  setPlayerKernel: (kernel: 'hls.js' | 'origin') => void;

  // 跳过片头片尾开关
  skipIntroEnabled: boolean;
  setSkipIntroEnabled: (enabled: boolean) => void;

  // 画面比例（0: 原始比例, 1: 全屏幕）
  aspectRatio: '0' | '1';
  setAspectRatio: (ratio: '0' | '1') => void;

  // ==================== 错误处理 ====================
  // 错误类型
  errorType: 'play' | 'network' | 'decode' | 'session' | 'vip' | null;
  // 错误信息对象
  errorInfo: ErrorInfo | null;
  // 错误码
  errorCode: string | number | null;
  // 是否在卸载时保留错误信息（用于跳转到其他页面后再返回的场景）
  keepErrorOnUnmount: boolean;
  // 延迟清除错误的定时器
  resetErrorTimer: ReturnType<typeof setTimeout> | null;
  setKeepErrorOnUnmount: (keep: boolean) => void;
  setError: (
    errorCode?: string | number | null,
    errorType?: 'play' | 'network' | 'decode' | 'session' | 'vip' | null,
    errorInfo?: ErrorInfo | null
  ) => void;
  clearError: () => void;
  cancelResetError: () => void;

  // ==================== 工具方法 ====================
  // 重置状态
  reset: () => void;

  // 格式化时间
  formatTime: (seconds: number) => string;

  // 获取播放进度百分比
  getProgress: () => number;

  // 获取可用的清晰度列表
  getAvailableQualities: () => Quality[];

  // ==================== 内部缓存（用于性能优化）====================
  // 清晰度列表缓存（内部使用，不对外暴露）
  cachedQualities?: Quality[];
  cachedQualitiesHash?: string;
}

// 从 localStorage 读取用户设置的画质（内部格式），如果无效则使用默认值
const savedQuality = isBrowser ? localStorage.getItem(PLAYER_STORAGE_KEYS.QUALITY) : null;
const defaultQuality: Quality =
  savedQuality && VALID_INTERNAL_QUALITIES.includes(savedQuality.toLowerCase())
    ? savedQuality.toLowerCase()
    : DEFAULT_QUALITY;

const initialState = {
  playbackStateVersion: 0,
  currentTime: 0,
  totalTime: 0,
  buffered: 0,
  cid: null as string | null,
  vid: null as string | null,
  payStatus: -1,
  videoTitle: '',
  videoInfo: null as any,
  videoListInfo: null as any | null,
  previewTime: 0,
  quality: defaultQuality as Quality, // 内部格式（如 'shd'），从 localStorage 读取
  speed: '1x' as PlaybackSpeed,
  isOnError: false,
  // 字幕完整状态
  subtitleList: [] as SubtitleItem[],
  currentSubtitleIndex: 0,
  subtitleSize: 'middle' as SubtitleSize,

  // 字幕兼容状态
  subtitleEnabled: true,
  subtitleLanguage: 'zh' as SubtitleLanguage,

  audioLanguage: 'zh' as AudioLanguage,
  skipEnd: 0,
  skipHeadBegin: 0,
  skipStart: 0,
  skipTailEnd: 0,
  playerInstance: null as any | null,
  // 从 localStorage 读取配置
  playerKernel: ((isBrowser ? localStorage.getItem(PLAYER_STORAGE_KEYS.KERNEL) : null) === 'origin'
    ? 'origin'
    : 'hls.js') as 'hls.js' | 'origin',
  skipIntroEnabled: isBrowser && localStorage.getItem(PLAYER_STORAGE_KEYS.SKIP_INTRO) === '1',
  aspectRatio: ((isBrowser ? localStorage.getItem(PLAYER_STORAGE_KEYS.ASPECT_RATIO) : null) === '1' ? '1' : '0') as
    | '0'
    | '1',
  errorType: null as 'play' | 'network' | 'decode' | 'session' | 'vip' | null,
  errorInfo: null as ErrorInfo | null,
  errorCode: null as string | number | null,
  keepErrorOnUnmount: false,
  resetErrorTimer: null as ReturnType<typeof setTimeout> | null,
  hasNextEpisode: false,
};

export const usePlayerStore = create<PlayerState>((set, get) => ({
  ...initialState,

  // ==================== 状态设置 ====================
  setPlaybackStateVersion: () => {
    set((s) => ({ playbackStateVersion: s.playbackStateVersion + 1 }));
  },
  getPlaybackState: () => {
    return get().playerInstance?.currentVideoInfo?.state;
  },
  setPreviewTime(time) {
    set({ previewTime: time });
  },
  setCurrentTime: (time) => {
    set({ currentTime: time });

    // 触发时间更新事件
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('player:timeupdate', { detail: { time } }));
    }
  },
  resetError() {
    const { keepErrorOnUnmount } = get();
    if (keepErrorOnUnmount) {
      // alert('');
      // 如果标记为保留，则不清除，但要重置标记，以免永久保留
      set({ keepErrorOnUnmount: false });
      logger.log('[PlayerStore] resetError 被跳过，因为 keepErrorOnUnmount 为 true');
      return;
    }

    // 延迟清除错误，以处理 StrictMode 下的快速卸载/挂载
    const timer = setTimeout(() => {
      set({ isOnError: false, errorType: null, errorInfo: null, errorCode: null, resetErrorTimer: null });
    }, 0);
    set({ resetErrorTimer: timer });
  },
  cancelResetError() {
    const { resetErrorTimer } = get();
    if (resetErrorTimer) {
      clearTimeout(resetErrorTimer);
      set({ resetErrorTimer: null });
    }
  },
  setKeepErrorOnUnmount(keep) {
    set({ keepErrorOnUnmount: keep });
  },
  setIsOnError(isOnError) {
    set({ isOnError });
  },
  setTotalTime: (time) => {
    set({ totalTime: time });
  },

  setBuffered: (progress) => {
    set({ buffered: progress });
  },

  setVideoInfo: (cid, vid, title = '') => {
    set({ cid, vid, videoTitle: title });
  },

  setVideoDetailInfo: (info) => {
    set({ videoInfo: info });
  },

  setVideoListInfo: (info) => {
    set({ videoListInfo: info });
  },
  setPayStatus: (payStatus) => {
    set({ payStatus });
  },
  // ==================== 播放控制 ====================
  play: async () => {
    const { playerInstance } = get();
    if (playerInstance && typeof playerInstance.play === 'function') {
      try {
        await playerInstance.play();
        return { success: true, message: '播放成功' };
      } catch (error) {
        logger.error('[PlayerStore] 播放失败:', error);
        return {
          success: false,
          message: '播放失败',
          error: error instanceof Error ? error : new Error(String(error)),
        };
      }
    }
    return { success: false, message: '播放器实例未初始化' };
  },

  pause: async () => {
    const { playerInstance } = get();
    if (playerInstance && typeof playerInstance.pause === 'function') {
      try {
        await playerInstance.pause();
        return { success: true, message: '暂停成功' };
      } catch (error) {
        logger.error('[PlayerStore] 暂停失败:', error);
        return {
          success: false,
          message: '暂停失败',
          error: error instanceof Error ? error : new Error(String(error)),
        };
      }
    }
    return { success: false, message: '播放器实例未初始化' };
  },

  togglePlayPause: async () => {
    const currentState = get().getPlaybackState();
    if (currentState === PlayerInstanceVideoState.Playing) {
      return get().pause();
    }
    return get().play();
  },

  /**
   * 停止播放
   */
  stop: async () => {
    const { playerInstance } = get();
    if (playerInstance && typeof playerInstance.stop === 'function') {
      try {
        const res = await playerInstance.stop();
        return { success: true, message: '停止成功', res };
      } catch (error) {
        logger.error('[PlayerStore] 停止失败:', error);
        return {
          success: false,
          message: '停止失败',
          error: error instanceof Error ? error : new Error(String(error)),
        };
      }
    }
    return { success: false, message: '播放器实例未初始化或不支持 stop 方法' };
  },

  /**
   * 跳转到指定时间
   * 参考 tv-linux/src/views/newParticular/newParticular.js:2387
   */
  seek: (time) => {
    const { playerInstance } = get();
    // 实际跳转时间需要加上片头时长（使用 skipStart 作为片头结束时间点）
    const actualTime = time;
    // 先更新状态，确保UI立即响应
    set({ currentTime: time });
    if (playerInstance && typeof playerInstance.seek === 'function') {
      try {
        playerInstance.seek(actualTime);
        logger.log('[PlayerStore] Seek 到:', time, '实际时间:', actualTime);
      } catch (error) {
        logger.error('[PlayerStore] 跳转失败:', error);
      }
    }
  },

  /**
   * 相对跳转（快进/快退）
   * 参考 tv-linux/src/views/newParticular/newParticular.js:2387-2444
   * 默认每次10秒
   */
  seekRelative: (seconds) => {
    const { currentTime, totalTime } = get();
    const newTime = Math.max(0, Math.min(totalTime, currentTime + seconds));
    get().seek(newTime);

    logger.log(`[PlayerStore] ${seconds > 0 ? '快进' : '快退'} ${Math.abs(seconds)} 秒`);
  },

  /**
   * 播放下一集
   * 参考 tv-linux/src/views/newParticular/newParticular.js:5742-5849
   * 使用通用方法获取下一集（优先 getNextVid，备选播放列表）
   * @returns 返回结果对象，包含 success、cid、vid、message 等信息，失败时返回 null
   */
  playNext: () => {
    const { cid, vid, videoListInfo } = get();

    logger.log('[PlayerStore] playNext 被调用，当前状态:', {
      cid,
      vid,
      hasVideoListInfo: !!videoListInfo,
      playListLength: videoListInfo?.play_list_info?.length,
    });

    if (!cid || !vid) {
      logger.warn('[PlayerStore] 缺少 cid 或 vid，无法播放下一集');
      return null;
    }

    // 使用通用方法获取下一集（优先 getNextVid，备选播放列表）
    const nextEpisode = getNextEpisode(cid, vid, videoListInfo);

    if (!nextEpisode.hasNext || !nextEpisode.nextVid) {
      logger.log('[PlayerStore] 没有下一集');
      return { success: false, type: 'noNext' };
    }

    const { nextVid, nextCid = cid } = nextEpisode;

    // 检查下下一集是否存在
    const nextNextEpisode = getNextEpisode(nextCid, nextVid, videoListInfo);
    const hasNextEpisode = nextNextEpisode.hasNext;

    set({ hasNextEpisode });

    logger.log('[PlayerStore] 切换到下一集:', {
      cid: nextCid,
      vid: nextVid,
      hasNextEpisode,
      source: nextEpisode.source,
    });

    return { success: true, cid: nextCid, vid: nextVid, hasNextEpisode };
  },

  /**
   * 播放上一集
   * 参考 tv-linux/src/views/newParticular/newParticular.js
   * 从 videoListInfo 中获取上一集并切换
   * @returns 返回结果对象，包含 success、cid、vid、message 等信息，失败时返回 null
   */
  playPrevious: () => {
    const { cid, vid, videoListInfo } = get();

    if (!cid || !vid) {
      logger.warn('[PlayerStore] 缺少 cid 或 vid，无法播放上一集');
      return null;
    }

    if (!videoListInfo?.play_list_info || !Array.isArray(videoListInfo.play_list_info)) {
      logger.warn('[PlayerStore] 播放列表信息不存在，无法播放上一集');
      return null;
    }

    const playList = videoListInfo.play_list_info;
    const currentIndex = playList.findIndex((video: any) => video.vid === vid);

    if (currentIndex === -1) {
      logger.warn('[PlayerStore] 当前视频不在播放列表中');
      return null;
    }

    // 检查是否有上一集
    if (currentIndex > 0) {
      // 有上一集，切换到上一集
      const previousVideo = playList[currentIndex - 1];
      const previousVid = previousVideo.vid;
      const previousCid = previousVideo.cid || cid; // 如果上一集有 cid 则使用，否则使用当前 cid

      logger.log('[PlayerStore] 切换到上一集:', { cid: previousCid, vid: previousVid });
      return { success: true, cid: previousCid, vid: previousVid };
    }
    // 没有上一集
    logger.log('[PlayerStore] 已经是第一集');
    return { success: false, message: '已经是第一集' };
  },

  // ==================== 设置控制 ====================

  /**
   * 切换清晰度
   * 参考 tv-linux/src/views/newParticular/newParticular.js:2900-2918
   * quality 参数支持内部格式（如 'fhd', 'shd'）或显示格式（如 '1080P', '720P'）
   * 内部统一使用内部格式存储
   * @param options 配置对象
   * @param options.quality 清晰度值
   * @param options.showToast 是否显示 toast 提示，默认为 true
   */
  setQuality: async ({ quality, showToast = true }) => {
    const { playerInstance, videoInfo } = get();

    // 判断是否为内部格式
    const isInternalFormat = VALID_INTERNAL_QUALITIES.includes(quality.toLowerCase());

    // 确定内部格式和显示格式
    let internalQuality: string;
    let displayQuality: string;

    if (isInternalFormat) {
      internalQuality = quality.toLowerCase();
      displayQuality = internalToDisplayQuality(internalQuality) || quality;
    } else {
      // 显示格式转内部格式
      const internal = Object.entries(INTERNAL_TO_DISPLAY_QUALITY_MAP).find(([, display]) => display === quality)?.[0];
      internalQuality = internal || DEFAULT_QUALITY;
      displayQuality = quality;
    }

    // 更新 store 状态（使用内部格式）
    set({ quality: internalQuality });

    if (!playerInstance) {
      // 仅保存到 localStorage，不切换播放器
      if (isBrowser) {
        localStorage.setItem(PLAYER_STORAGE_KEYS.QUALITY, internalQuality);
      }
      return { success: true, message: '设置已保存（播放器未初始化）' };
    }

    // 必须要有 definitionList
    if (!videoInfo?.definitionList || !Array.isArray(videoInfo.definitionList)) {
      // 仅保存到 localStorage
      if (isBrowser) {
        localStorage.setItem(PLAYER_STORAGE_KEYS.QUALITY, internalQuality);
      }
      return { success: true, message: '设置已保存（视频清晰度列表未加载）' };
    }

    // 调用播放器实例切换清晰度
    try {
      if (typeof playerInstance.setLevel !== 'function') {
        return { success: false, message: '播放器不支持切换清晰度' };
      }

      // 确定播放器使用的清晰度标识（defn）
      let defn: string;

      if (quality === 'auto') {
        defn = 'auto';
      } else {
        // 查找匹配的清晰度定义（通过内部格式 name 或显示格式 cname/resolution）
        const foundDefinition = videoInfo.definitionList.find(
          (def: any) =>
            def.name === internalQuality || def.cname === displayQuality || def.resolution === displayQuality
        );

        if (!foundDefinition) {
          // 保存用户偏好，但播放器可能不支持该清晰度
          if (isBrowser) {
            localStorage.setItem(PLAYER_STORAGE_KEYS.QUALITY, internalQuality);
          }
          logger.warn(`[PlayerStore] 清晰度 ${quality} 不在当前视频可用列表中，已保存为用户偏好`);
          return { success: true, message: '设置已保存（当前视频不支持该清晰度）' };
        }

        // 使用播放器内部的 name 字段（如 'fhd', 'shd', 'hd', 'sd', 'ld'）
        defn = foundDefinition.name;
      }

      // 调用播放器切换清晰度
      await playerInstance.setLevel({ defn });

      // 保存用户偏好（使用内部格式）
      if (isBrowser) {
        localStorage.setItem(PLAYER_STORAGE_KEYS.QUALITY, internalQuality);
      }

      logger.log('[PlayerStore] 清晰度已切换:', { quality, internalQuality, defn });

      // 显示Toast提示（多语言，使用显示格式）- 仅在 showToast 为 true 时显示
      if (showToast) {
        toast.show(i18n.t('player.qualitySwitching', { quality: displayQuality }));
      }

      // 触发事件通知
      if (isBrowser) {
        window.dispatchEvent(
          new CustomEvent('player:qualitychange', {
            detail: { quality: internalQuality, displayQuality, defn, success: true },
          })
        );
      }

      return { success: true, message: `清晰度已切换至 ${displayQuality}` };
    } catch (error) {
      logger.error('[PlayerStore] 切换清晰度失败:', error);

      // 触发失败事件
      if (isBrowser) {
        window.dispatchEvent(
          new CustomEvent('player:qualitychange', {
            detail: { quality: internalQuality, success: false, error },
          })
        );
      }

      return {
        success: false,
        message: '切换清晰度失败',
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  },

  /**
   * 切换倍速
   * 参考 tv-linux/src/views/newParticular/newParticular.js:2984-2998
   * 根据附图需求：
   * 1. 切换3.0x倍速后，播放下一集或切换其他视频时，记住上次选择，播放器倍速为3.0x
   * 2. 切换2.0x倍速后，退出播放器重新进入播放器时，倍速恢复初始1.0x
   * 3. 切换3.0x倍速后，重启APP进入播放器时，倍速恢复初始1.0x
   */
  setSpeed: async (speed) => {
    const { playerInstance } = get();
    set({ speed });

    if (!playerInstance) {
      return { success: false, message: '播放器实例未初始化' };
    }

    // 调用播放器实例切换倍速
    try {
      const rate = parseFloat(speed.replace('x', ''));

      // 播放器使用 playbackRate 属性设置倍速
      playerInstance.playbackRate = String(rate);

      logger.log('[PlayerStore] 倍速已切换:', speed);

      // 保存倍速到sessionStorage（仅在当前会话中有效）
      // 根据需求：所有倍速调整都需要记住，但重启APP后恢复1.0x
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('playbackSpeed', speed);
      }

      // 触发事件通知
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('player:speedchange', {
            detail: { speed, rate, success: true },
          })
        );
      }

      return { success: true, message: `倍速已切换至 ${speed}` };
    } catch (error) {
      logger.error('[PlayerStore] 切换倍速失败:', error);

      // 触发失败事件
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('player:speedchange', {
            detail: { speed, success: false, error },
          })
        );
      }

      return {
        success: false,
        message: '切换倍速失败',
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  },

  // ==================== 字幕功能完整实现 ====================

  /**
   * 初始化字幕列表
   * 参考 tv-linux/src/views/newParticular/newParticular.js:5014-5101
   */
  initSubtitleList: (list, channelId) => {
    // 添加"无字幕"选项
    const noSubtitle = createNoSubtitleItem();
    const fullList = [noSubtitle, ...list];

    // 根据渠道过滤字幕列表
    const filteredList = filterSubtitlesByChannel(fullList, channelId);

    // 加载用户偏好
    const preferences = loadSubtitlePreferences();

    // 获取当前应该选中的字幕索引
    const subtitleIndex = getSubtitleIndex(filteredList, preferences.language, preferences.setupLanguage);

    // 加载字幕大小偏好
    get().loadSubtitlePreferences();

    // 更新状态
    set({
      subtitleList: filteredList,
      currentSubtitleIndex: subtitleIndex,
    });

    // 应用字幕设置到播放器
    get().applySubtitleSettings();
  },

  /**
   * 切换字幕语言
   * 参考 tv-linux/src/views/newParticular/newParticular.js:3069-3117
   */
  setSubtitleLanguage: async (index) => {
    const { subtitleList } = get();

    if (!subtitleList || index < 0 || index >= subtitleList.length) {
      const message = '无效的字幕索引';
      logger.warn('[PlayerStore]', message, index);
      return { success: false, message };
    }

    const currentSubtitle = subtitleList[index];

    try {
      // 保存用户偏好
      saveSubtitleLanguagePreference(currentSubtitle.lang);

      // 更新状态
      set({
        currentSubtitleIndex: index,
      });

      // 应用字幕设置到播放器
      await get().applySubtitleSettings();

      logger.log('[PlayerStore] 字幕语言已切换:', currentSubtitle.name);

      // 触发事件通知
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('player:subtitlechange', {
            detail: { subtitle: currentSubtitle, index, success: true },
          })
        );
      }

      return { success: true, message: `字幕已切换至 ${currentSubtitle.name}` };
    } catch (error) {
      logger.error('[PlayerStore] 切换字幕语言失败:', error);

      // 触发失败事件
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('player:subtitlechange', {
            detail: { subtitle: currentSubtitle, index, success: false, error },
          })
        );
      }

      return {
        success: false,
        message: '切换字幕语言失败',
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  },

  /**
   * 设置字幕大小
   * 参考 tv-linux/src/views/newParticular/newParticular.js:3251-3273
   */
  setSubtitleSize: async (size) => {
    const { playerInstance } = get();
    const sizeConfig = getSubtitleSizeConfig(size);

    try {
      // 保存用户偏好
      saveSubtitleFontsizePreference(sizeConfig.renderFontSize);

      // 更新状态
      set({ subtitleSize: size });

      // 应用字幕样式到播放器
      if (playerInstance?.plugins?.subtitle) {
        const style = createSubtitleStyle(size);
        const subtitlePlugin = playerInstance.plugins.subtitle;

        // 设置字幕样式
        subtitlePlugin.renderer?.setStyle?.(style);

        // 触发重新渲染
        subtitlePlugin.handleResize?.(style);

        logger.log('[PlayerStore] 字幕大小已更新:', size);

        // 触发事件通知
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('player:subtitlesizechange', {
              detail: { size, config: sizeConfig, success: true },
            })
          );
        }

        return { success: true, message: `字幕大小已设置为 ${size}` };
      }
      return { success: false, message: '播放器或字幕插件未初始化' };
    } catch (error) {
      logger.error('[PlayerStore] 设置字幕大小失败:', error);

      // 触发失败事件
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('player:subtitlesizechange', {
            detail: { size, success: false, error },
          })
        );
      }

      return {
        success: false,
        message: '设置字幕大小失败',
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  },

  /**
   * 应用字幕设置到播放器
   * 参考 tv-linux/src/views/newParticular/newParticular.js:3085-3116
   */
  applySubtitleSettings: () => {
    const { playerInstance, subtitleList, currentSubtitleIndex, subtitleSize } = get();

    if (!playerInstance?.plugins?.subtitle) {
      logger.warn('[PlayerStore] 播放器或字幕插件未初始化');
      return;
    }

    if (!subtitleList || subtitleList.length === 0) {
      logger.warn('[PlayerStore] 字幕列表为空');
      return;
    }

    const currentSubtitle = subtitleList[currentSubtitleIndex];
    if (!currentSubtitle) {
      logger.warn('[PlayerStore] 当前字幕不存在');
      return;
    }

    const subtitlePlugin = playerInstance.plugins.subtitle;

    try {
      // 如果是"无字幕"或URL为空，隐藏字幕
      if (!currentSubtitle.url) {
        subtitlePlugin.hideSubTitle?.();
        return;
      }

      // 处理字幕URL
      const url = processSubtitleUrl(currentSubtitle.url);
      const isVTT = isVTTFormat(url);

      // 先设置字幕URL和基本配置（不依赖视频尺寸）
      subtitlePlugin.setSubtitleURL?.([url], isVTT ? 0 : 1);
      subtitlePlugin.setStickTo?.('container');

      // 尝试设置字幕位置（如果有视频尺寸信息）
      const videoContainer = playerInstance.container;
      const { videoInfo } = get(); // 从 store 获取 videoInfo（现在直接是 parseData）

      if (videoInfo && videoContainer) {
        // videoInfo 现在直接是 parseData，不需要再解析
        const { videoWidth, videoHeight } = videoInfo;

        if (videoWidth && videoHeight) {
          const videoDOMWidth = videoContainer.getClientRects()[0]?.width;
          const videoDOMHeight = videoContainer.getClientRects()[0]?.height;

          if (videoDOMWidth && videoDOMHeight) {
            // 计算字幕位置
            const captionTopHPercent = currentSubtitle.captionTopHPercent || 90;
            const position = calculateSubtitlePosition(
              videoWidth,
              videoHeight,
              videoDOMWidth,
              videoDOMHeight,
              captionTopHPercent
            );
            // position 对象已经包含 {x, y, offsetY}，直接传递
            subtitlePlugin.setTextTrackPos?.(position);
          } else {
            logger.warn('[PlayerStore] ⚠️ 无法获取容器尺寸，使用默认位置');
          }
        } else {
          logger.warn('[PlayerStore] ⚠️ 无法获取视频尺寸，使用默认位置');
        }
      } else {
        logger.warn('[PlayerStore] ⚠️ videoInfo 不存在，字幕将使用默认位置');
        // 使用默认位置（底部居中）
        subtitlePlugin.setTextTrackPos?.({
          x: 'middle',
          y: 'max',
          offsetY: -100, // 距离底部100px
        });
      }

      // 设置字幕样式
      const style = createSubtitleStyle(subtitleSize);
      subtitlePlugin.renderer?.setStyle?.(style);

      // 显示字幕
      subtitlePlugin.showSubTitle?.();

      logger.log('[PlayerStore] 字幕设置已应用:', currentSubtitle.name);
    } catch (error) {
      logger.error('[PlayerStore] 应用字幕设置失败:', error);
    }
  },

  /**
   * 加载用户偏好
   */
  loadSubtitlePreferences: () => {
    const preferences = loadSubtitlePreferences();

    // 从localStorage加载字幕大小
    if (preferences.fontSize) {
      const fontSize = parseFloat(preferences.fontSize);

      // 根据字号查找对应的大小类型
      const allSizes: SubtitleSize[] = ['small', 'middle', 'large', 'larger', 'largest'];
      for (const size of allSizes) {
        const config = getSubtitleSizeConfig(size);
        if (config.renderFontSize === fontSize) {
          set({ subtitleSize: size });
          break;
        }
      }
    }
  },

  /**
   * 清除字幕状态
   */
  clearSubtitleState: () => {
    set({
      subtitleList: [],
      currentSubtitleIndex: 0,
      subtitleSize: 'middle',
    });
  },

  // ==================== 字幕兼容方法（保留旧接口）====================

  setSubtitleEnabled: async (enabled) => {
    set({ subtitleEnabled: enabled });

    // 直接控制播放器字幕显示
    const { playerInstance } = get();
    if (playerInstance?.plugins?.subtitle) {
      try {
        const subtitlePlugin = playerInstance.plugins.subtitle;
        if (enabled) {
          subtitlePlugin.showSubTitle?.();
        } else {
          subtitlePlugin.hideSubTitle?.();
        }

        logger.log('[PlayerStore] 字幕显示已', enabled ? '开启' : '关闭');

        // 触发事件通知
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('player:subtitleenabledchange', {
              detail: { enabled, success: true },
            })
          );
        }

        return { success: true, message: `字幕已${enabled ? '开启' : '关闭'}` };
      } catch (error) {
        logger.error('[PlayerStore] 切换字幕显示失败:', error);

        // 触发失败事件
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('player:subtitleenabledchange', {
              detail: { enabled, success: false, error },
            })
          );
        }

        return {
          success: false,
          message: '切换字幕显示失败',
          error: error instanceof Error ? error : new Error(String(error)),
        };
      }
    }
    return { success: false, message: '播放器或字幕插件未初始化' };
  },

  setAudioLanguage: async (lang) => {
    const { playerInstance } = get();
    set({ audioLanguage: lang });

    if (!playerInstance) {
      return { success: false, message: '播放器实例未初始化' };
    }

    if (typeof playerInstance.setAudioLanguage === 'function') {
      try {
        await playerInstance.setAudioLanguage(lang);

        logger.log('[PlayerStore] 音频语言已切换:', lang);

        // 触发事件通知
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('player:audiolanguagechange', {
              detail: { lang, success: true },
            })
          );
        }

        return { success: true, message: `音频语言已切换至 ${lang}` };
      } catch (error) {
        logger.error('[PlayerStore] 切换音频语言失败:', error);

        // 触发失败事件
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('player:audiolanguagechange', {
              detail: { lang, success: false, error },
            })
          );
        }

        return {
          success: false,
          message: '切换音频语言失败',
          error: error instanceof Error ? error : new Error(String(error)),
        };
      }
    } else {
      return { success: false, message: '播放器不支持切换音频语言' };
    }
  },

  setSkipIntro: (skipEnd, skipHeadBegin, skipStart, skipTailEnd) => {
    set({ skipEnd, skipHeadBegin, skipStart, skipTailEnd });
  },

  setHasNextEpisode: (hasNextEpisode) => {
    set({ hasNextEpisode });
  },

  // ==================== 播放器实例管理 ====================
  setPlayerInstance: (instance) => {
    set({ playerInstance: instance });

    // 恢复保存的倍速（所有倍速都会被保存）
    if (instance && isBrowser) {
      const savedSpeed = sessionStorage.getItem('playbackSpeed');
      if (savedSpeed && savedSpeed !== '1x') {
        // 延迟恢复倍速，确保播放器已完全初始化
        setTimeout(() => {
          const rate = parseFloat(savedSpeed.replace('x', ''));
          // 使用临时变量避免直接修改参数
          const playerInstance = instance;
          playerInstance.playbackRate = String(rate);
          set({ speed: savedSpeed as PlaybackSpeed });
          logger.log('[PlayerStore] 已恢复保存的倍速:', savedSpeed);
        }, 500);
      }

      // 如果视频信息已加载，尝试应用用户设置的清晰度
      const { videoInfo, quality: currentQuality } = get();
      if (videoInfo?.definitionList && videoInfo.definitionList.length > 0) {
        // 延迟应用清晰度，确保播放器已完全初始化
        setTimeout(() => {
          const currentState = get();
          // 读取保存的清晰度（内部格式）
          const savedQuality = localStorage.getItem(PLAYER_STORAGE_KEYS.QUALITY);
          if (savedQuality && VALID_INTERNAL_QUALITIES.includes(savedQuality.toLowerCase())) {
            const targetQuality = savedQuality.toLowerCase();

            // 如果目标清晰度与当前清晰度不一致，尝试切换
            if (targetQuality !== currentQuality) {
              // 检查目标清晰度是否在可用列表中
              const qualityExists = videoInfo.definitionList.some((def: any) => def.name === targetQuality);

              if (qualityExists) {
                currentState.setQuality({ quality: targetQuality }).catch((error) => {
                  logger.warn('[PlayerStore] 播放器实例初始化后设置清晰度失败:', error);
                });
                logger.log('[PlayerStore] 播放器实例初始化后已应用用户设置的清晰度:', targetQuality);
              }
            }
          }
        }, 500);
      }
    }

    // 注意：播放器实例已通过 PlayerController 暴露到 window.player
    // 这里不再重复暴露，避免覆盖控制器实例
  },

  // ==================== 播放器事件管理 ====================
  /**
   * 监听播放器事件
   * @param eventName 事件名称
   * @param handler 事件处理函数
   */
  onPlayerEvent: (eventName, handler) => {
    const { playerInstance } = get();
    if (!playerInstance) {
      logger.warn('[PlayerStore] 播放器实例未初始化，无法监听事件:', eventName);
      return;
    }

    if (typeof playerInstance.on === 'function') {
      try {
        playerInstance.on(eventName, handler);
        logger.log('[PlayerStore] 已监听播放器事件:', eventName);
      } catch (error) {
        logger.error('[PlayerStore] 监听播放器事件失败:', eventName, error);
      }
    } else {
      logger.warn('[PlayerStore] 播放器实例不支持事件监听');
    }
  },

  /**
   * 移除播放器事件监听
   * @param eventName 事件名称
   * @param handler 事件处理函数
   */
  offPlayerEvent: (eventName, handler) => {
    const { playerInstance } = get();
    if (!playerInstance) {
      logger.warn('[PlayerStore] 播放器实例未初始化');
      return;
    }

    if (typeof playerInstance.off === 'function') {
      try {
        playerInstance.off(eventName, handler);
        logger.log('[PlayerStore] 已移除播放器事件监听:', eventName);
      } catch (error) {
        logger.error('[PlayerStore] 移除播放器事件监听失败:', eventName, error);
      }
    } else {
      logger.warn('[PlayerStore] 播放器实例不支持移除事件监听');
    }
  },

  /**
   * 触发播放器事件
   * @param eventName 事件名称
   * @param args 事件参数
   */
  emitPlayerEvent: (eventName, ...args) => {
    const { playerInstance } = get();
    if (!playerInstance) {
      logger.warn('[PlayerStore] 播放器实例未初始化');
      return;
    }

    if (typeof playerInstance.emit === 'function') {
      try {
        playerInstance.emit(eventName, ...args);
        logger.log('[PlayerStore] 已触发播放器事件:', eventName);
      } catch (error) {
        logger.error('[PlayerStore] 触发播放器事件失败:', eventName, error);
      }
    } else {
      // 如果播放器没有 emit 方法，使用自定义事件系统
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(`player:${eventName}`, { detail: args }));
      }
    }
  },

  // ==================== 播放器配置管理 ====================
  setPlayerKernel: (kernel) => {
    set({ playerKernel: kernel });
    if (isBrowser) {
      localStorage.setItem(PLAYER_STORAGE_KEYS.KERNEL, kernel);
    }
  },

  setSkipIntroEnabled: (enabled) => {
    set({ skipIntroEnabled: enabled });
    if (isBrowser) {
      localStorage.setItem(PLAYER_STORAGE_KEYS.SKIP_INTRO, enabled ? '1' : '0');
    }
  },

  setAspectRatio: (ratio) => {
    set({ aspectRatio: ratio });
    if (isBrowser) {
      localStorage.setItem(PLAYER_STORAGE_KEYS.ASPECT_RATIO, ratio);
    }
  },

  // ==================== 错误处理 ====================
  /**
   * 设置错误信息
   * @param errorCode 错误码（可选）
   * @param errorType 错误类型（可选）
   * @param errorInfo 错误信息对象（可选，包含所有错误信息）
   */
  setError: (errorCode = null, errorType = null, errorInfo = null) => {
    set({
      errorCode,
      errorType,
      errorInfo,
    });
    // 触发错误事件
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('player:error', {
          detail: { errorCode, errorType, errorInfo },
        })
      );
    }
  },

  /**
   * 清除错误信息
   */
  clearError: () => {
    set({
      // errorCode: null,
      // errorType: null,
      // errorInfo: null,
      // isOnError: false,
    });
  },

  // ==================== 工具方法 ====================
  reset: () => {
    // 清除字幕状态
    get().clearSubtitleState();

    set({
      ...initialState,
      playerInstance: get().playerInstance, // 保留播放器实例
      playerKernel: get().playerKernel, // 保留播放器内核配置
      skipIntroEnabled: get().skipIntroEnabled, // 保留跳过片头片尾配置
      aspectRatio: get().aspectRatio, // 保留画面比例配置
      cachedQualities: undefined, // 清除清晰度列表缓存
      cachedQualitiesHash: undefined, // 清除清晰度列表缓存哈希
      // errorType: null, // 清除错误类型
      // errorInfo: null, // 清除错误信息对象
      // errorCode: null, // 清除错误码
    });
  },

  formatTime: (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  },

  getProgress: () => {
    const { currentTime, totalTime } = get();
    return totalTime > 0 ? (currentTime / totalTime) * 100 : 0;
  },

  /**
   * 获取可用的清晰度列表
   * 完全基于 videoInfo.definitionList，返回所有可用的清晰度（使用 cname 或 resolution）
   * 如果没有 definitionList 则返回空数组
   * 使用缓存机制优化性能，避免重复计算
   */
  getAvailableQualities: () => {
    const { videoInfo, cachedQualities, cachedQualitiesHash } = get();

    if (!videoInfo?.definitionList || !Array.isArray(videoInfo.definitionList)) {
      return [];
    }

    // 生成缓存键（基于 definitionList 的内容）
    const currentHash = JSON.stringify(
      videoInfo.definitionList.map((def: DefinitionItem) => ({
        name: def.name,
        cname: def.cname,
        resolution: def.resolution,
      }))
    );

    // 如果缓存有效，直接返回
    if (cachedQualities && cachedQualitiesHash === currentHash) {
      return cachedQualities;
    }

    // 使用 constants.ts 中的 getQualityWeight 函数

    // 从 definitionList 中提取清晰度，使用 cname 或 resolution
    // 保留所有清晰度，不进行过滤
    const qualitiesWithWeight: Array<{ quality: string; weight: number }> = videoInfo.definitionList
      .map((def: DefinitionItem) => {
        // 优先使用 cname，如果没有则使用 resolution
        const quality = def.cname || def.resolution;
        if (!quality) {
          return null;
        }

        const weight = getQualityWeight(quality, def.name);
        return { quality, weight };
      })
      .filter(
        (item: { quality: string; weight: number } | null): item is { quality: string; weight: number } =>
          item !== null && item.quality !== ''
      );

    // 去重（按 quality 去重，保留权重最高的）
    const qualityMap = new Map<string, number>();
    qualitiesWithWeight.forEach(({ quality, weight }) => {
      const existingWeight = qualityMap.get(quality);
      if (!existingWeight || weight > existingWeight) {
        qualityMap.set(quality, weight);
      }
    });

    // 转换为数组并按权重从高到低排序
    const uniqueQualities = Array.from(qualityMap.entries())
      .sort(([, weightA], [, weightB]) => weightB - weightA)
      .map(([quality]) => quality);

    // 更新缓存
    set({
      cachedQualities: uniqueQualities,
      cachedQualitiesHash: currentHash,
    });

    return uniqueQualities;
  },
}));
